import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { animes, heroSlides } from "@/mocks/anime";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused]);

  const slide = heroSlides[index];
  const poster = animes.find((item) => item.id === slide.animeId);

  const goTo = (next: number) => {
    setIndex((next + heroSlides.length) % heroSlides.length);
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[500px] w-full md:h-[600px]">
        {heroSlides.map((item, slideIndex) => (
          <img
            key={item.id}
            src={item.image}
            alt={`${item.title} 主视觉海报`}
            title={`${item.title} - 樱花动漫官网推荐`}
            className={[
              "absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-700",
              slideIndex === index ? "opacity-100" : "opacity-0",
            ].join(" ")}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/20" />

        <div className="absolute inset-0">
          <div className="mx-auto flex h-full w-full max-w-[1400px] items-center px-4 pb-16 pt-10 md:px-8 md:pb-0">
            <div className="grid w-full grid-cols-1 items-center gap-10 lg:grid-cols-[1.35fr_0.65fr]">
              <div key={slide.id} className="animate-fade-up">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-300/50 bg-primary-500/15 px-3 py-1 font-label text-[11px] uppercase tracking-[0.18em] text-primary-200">
                  <i className="ri-heart-3-fill text-xs" />
                  本周焦点 · Sakura Pick
                </span>
                <h1 className="mt-4 text-3xl font-bold leading-tight text-background-50 md:text-5xl">
                  <span className="sr-only">樱花动漫官网 - </span>
                  {slide.title}
                </h1>
                <p className="mt-2 text-base text-primary-100 md:text-lg">{slide.subtitle}</p>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-[15px]">
                  {slide.description}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  {slide.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category?genre=${encodeURIComponent(tag)}`}
                      className="whitespace-nowrap rounded-md border border-background-50/25 bg-background-50/10 px-2.5 py-1 text-xs text-background-100 backdrop-blur-sm transition-colors hover:border-primary-300 hover:text-primary-200"
                    >
                      {tag}
                    </Link>
                  ))}
                  <span className="flex items-center gap-1 rounded-md bg-background-50/10 px-2.5 py-1 font-label text-xs text-primary-200 backdrop-blur-sm">
                    <i className="ri-star-fill text-primary-300" />
                    {slide.rating.toFixed(1)}
                  </span>
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${slide.animeId}`}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-play-fill text-base" />
                    立即观看
                  </Link>
                  <Link
                    to="/category"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm text-background-50 backdrop-blur-sm transition-colors hover:border-background-50 hover:bg-background-50/10"
                  >
                    浏览全部动漫
                    <i className="ri-arrow-right-line text-sm" />
                  </Link>
                </div>
              </div>

              {poster && (
                <div className="hidden justify-end lg:flex">
                  <Link
                    to={`/detail/${poster.id}`}
                    className="group relative block w-52 overflow-hidden rounded-lg border border-background-50/20 bg-background-50/5 p-3 backdrop-blur-md xl:w-60"
                  >
                    <div className="relative aspect-[3/4] w-full overflow-hidden rounded-md">
                      <img
                        src={poster.cover}
                        alt={`${poster.title} 封面`}
                        title={`${poster.title} - 详情页`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className="absolute bottom-2 left-2 rounded bg-foreground-950/70 px-2 py-0.5 font-label text-[11px] text-background-50 backdrop-blur-sm">
                        {poster.updateText}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-background-100">{poster.genres.join(" / ")}</span>
                      <span className="font-label text-[11px] text-primary-300">{poster.year}</span>
                    </div>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-0 right-0">
          <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between px-4 md:px-8">
            <div className="flex items-center gap-2">
              {heroSlides.map((item, dotIndex) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => goTo(dotIndex)}
                  aria-label={`切换到第 ${dotIndex + 1} 张推荐`}
                  className={[
                    "h-1.5 rounded-full transition-all",
                    dotIndex === index ? "w-8 bg-primary-400" : "w-4 bg-background-50/40 hover:bg-background-50/70",
                  ].join(" ")}
                />
              ))}
              <span className="ml-3 font-label text-xs text-background-200">
                0{index + 1} / 0{heroSlides.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => goTo(index - 1)}
                aria-label="上一张"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-arrow-left-line text-sm" />
              </button>
              <button
                type="button"
                onClick={() => goTo(index + 1)}
                aria-label="下一张"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-arrow-right-line text-sm" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}